import React from "react";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";
import { BsXCircle } from "react-icons/bs";

import { Order } from "../../../../models";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  createCustomInvoice,
  resetCreateCustomInvoice,
  setShowAddInvoiceModal,
} from "../../ordersSlice";

import "./CustomInvoiceForm.scss";

export interface CustomInvoiceFormModel {
  invoiceItems: InvoiceItem[];
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  basePrice: number;
}

const validationSchema = Yup.object().shape({
  invoiceItems: Yup.array().of(
    Yup.object().shape({
      description: Yup.string().required("Description is required"),
      quantity: Yup.number()
        .required("Quantity is required")
        .min(1, "Must be at least 1"),
      basePrice: Yup.number()
        .required("Price is required")
        .positive("Must be positive"),
    })
  ),
});

const initialValues: CustomInvoiceFormModel = {
  invoiceItems: [{ description: "", quantity: 1, basePrice: 0 }],
};

export interface CustomInvoiceFormProps {
  order: Order;
}

export function CustomInvoiceForm(props: CustomInvoiceFormProps) {
  const dispatch = useAppDispatch();
  const { order } = props;

  const createCustomInvoiceAPI = useAppSelector(
    (state) => state.orders.createCustomInvoiceAPI
  );

  React.useEffect(() => {
    if (!createCustomInvoiceAPI.loading && !!createCustomInvoiceAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (
      !createCustomInvoiceAPI.loading &&
      !createCustomInvoiceAPI.error &&
      createCustomInvoiceAPI.value
    ) {
      toast.success("Successfully created invoice.");
      dispatch(setShowAddInvoiceModal(undefined));
      dispatch(resetCreateCustomInvoice());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createCustomInvoiceAPI.loading,
    !!createCustomInvoiceAPI.error,
    !!createCustomInvoiceAPI.value,
  ]);

  const onCreateCustomInvoice = (form: CustomInvoiceFormModel) => {
    if (createCustomInvoiceAPI.loading) return;

    dispatch(
      createCustomInvoice({
        orderId: order.id,
        form,
      })
    );
  };

  return (
    <div className="custom-invoice-form-component">
      <Formik
        initialValues={initialValues}
        onSubmit={onCreateCustomInvoice}
        validationSchema={validationSchema}
      >
        {({ errors, touched, values }) => (
          <Form className="approve-order-form-component">
            <div className="row">
              <div className="label width-150">Description:</div>
              <div className="label width-50">Quantity:</div>
              <div className="label width-50">Price:</div>
            </div>

            <FieldArray name="invoiceItems">
              {({ remove, push }) => (
                <div>
                  {values.invoiceItems.length > 0 &&
                    values.invoiceItems.map((item, index) => (
                      <div className="row" key={index}>
                        <div className="field width-150">
                          <Field
                            className="text-input"
                            name={`invoiceItems.${index}.description`}
                            type="text"
                          />

                          <ErrorMessage
                            name={`invoiceItems.${index}.description`}
                            component="div"
                            className="field-error"
                          />
                        </div>

                        <div className="field width-50">
                          <Field
                            className="text-input"
                            name={`invoiceItems.${index}.quantity`}
                            type="number"
                          />

                          <ErrorMessage
                            name={`invoiceItems.${index}.quantity`}
                            component="div"
                            className="field-error"
                          />
                        </div>

                        <div className="field width-50">
                          <Field
                            className="text-input"
                            name={`invoiceItems.${index}.basePrice`}
                            type="number"
                          />

                          <ErrorMessage
                            name={`invoiceItems.${index}.basePrice`}
                            component="div"
                            className="field-error"
                          />
                        </div>

                        <div
                          className="secondary"
                          onClick={() => remove(index)}
                        >
                          <BsXCircle className="remove-icon" />
                        </div>
                      </div>
                    ))}

                  <div
                    className="add-item-button"
                    onClick={() =>
                      push({ description: "", quantity: 1, basePrice: 0 })
                    }
                  >
                    Add Item
                  </div>
                </div>
              )}
            </FieldArray>

            <button
              type="submit"
              className="add-button"
              // disabled={createCustomInvoiceAPI.loading}
            >
              Create Invoice
              {createCustomInvoiceAPI.loading && (
                <div style={{ marginLeft: 8 }}>
                  <Oval
                    color="#131313"
                    height={20}
                    width={20}
                    secondaryColor="#FBD20A"
                  />
                </div>
              )}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
}
